import { supabase } from '../../libs/supabaseClient';
import { ChatAction } from '../../types/chat';
import { baseMimeType } from '../../helpers/voiceNoteHelper';
import type { UiContextPayload } from './uiContext';

const CHATBOT_API_URL = process.env.REACT_APP_CHATBOT_URL || 'http://localhost:3005';

/**
 * Tipos de datos para el servicio de chat
 */

export interface ChatMessage {
  message: string;
  sessionId?: string;
  provider?: string;
  model?: string;
  temperature?: number;
}

export interface ChatAnswer {
  text: string;
  language: string;
  confidence?: number;
  needs_clarification?: boolean;
  clarification_question?: string | null;
}

export interface ChatFact {
  key: string;
  value: number | string | boolean | null;
  unit?: string;
}

export interface ChatSource {
  type: 'tool' | 'rag_chunk' | 'context';
  id: string;
  source_type?: string;
  source_id?: string;
  excerpt?: string;
  retrieved_at?: string;
}

export interface ChatToolUsed {
  name: string;
  args?: Record<string, unknown>;
  latency_ms?: number;
  cached?: boolean;
  success?: boolean;
}

export interface ChatMetadata {
  trace_id: string;
  iterations?: number;
  provider?: string;
  model?: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_usd?: number;
  latency_ms?: number;
}

export interface ChatSuccessResponse {
  success: true;
  data: {
    sessionId: string;
    answer: ChatAnswer;
    facts: ChatFact[];
    sources: ChatSource[];
    tools_used: ChatToolUsed[];
    metadata: ChatMetadata;
  };
  cached?: boolean;
}

export interface ChatErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    retryable: boolean;
    retry_after_seconds?: number | null;
    details?: unknown;
  };
  metadata: {
    trace_id: string;
  };
}

export type ChatResponse = ChatSuccessResponse | ChatErrorResponse;

/**
 * Eventos SSE para streaming
 */
export type SSEEventType =
  | 'start'
  | 'tool_started'
  | 'tool_completed'
  | 'tool_failed'
  | 'answer_delta'
  | 'done'
  | 'error';

export interface SSEEvent {
  event: SSEEventType;
  data: unknown;
}

/**
 * Envío streaming unificado (KAN-218): parámetros del turno.
 * `batchCount` = cantidad de mensajes mergeados en el turno (cola offline /
 * debounce) — el server lo usa para escalar las iteraciones del loop agéntico.
 */
export interface StreamTurnParams {
  message: string;
  sessionId: string;
  batchCount?: number;
  /** KAN-225: turno armado con la cola offline (solo visibilidad en el admin del bot). */
  offlineQueued?: boolean;
  voiceNoteCount?: number;
  transcriptionTraceIds?: string[];
  /**
   * Plataforma del cliente. Mobile la setea (via ChatService.configure) para que
   * el bot NO mande los botones ver→modal de operación (los modales viven en la
   * web); en mobile solo viaja el confirmar. Ausente = web (compat).
   */
  platform?: 'web' | 'mobile';
  /** Contexto de pantalla (página, selección, ficha, posición) — ver ChatUIContext. */
  uiContext?: UiContextPayload;
}

/** Callbacks del stream SSE para el envío unificado. */
export interface StreamTurnCallbacks {
  onDelta: (text: string) => void;
  onDone: (payload: { actions?: ChatAction[]; traceId?: string }) => void;
  onHandoff: (payload: { supportId?: string; status?: string }) => void;
  onBudgetWarning: (payload: { message?: string; percentage?: number; remaining?: number; limit?: number }) => void;
  onARGuide: (payload: unknown) => void;
}

export interface SSEStartData {
  sessionId: string;
  trace_id: string;
}

export interface SSEToolStartedData {
  name: string;
  args?: Record<string, unknown>;
}

export interface SSEToolCompletedData {
  name: string;
  result?: unknown;
  latency_ms: number;
  cached?: boolean;
}

export interface SSEToolFailedData {
  name: string;
  error: string;
  latency_ms: number;
}

export interface SSEAnswerDeltaData {
  delta: string;
}

export interface SSEDoneData {
  sessionId: string;
  trace_id: string;
  answer?: ChatAnswer;
  facts?: ChatFact[];
  sources?: ChatSource[];
  tools_used?: ChatToolUsed[];
  metadata?: ChatMetadata;
  actions?: ChatAction[];
}

export interface SSEErrorData {
  code: string;
  message: string;
}

/**
 * Callbacks para eventos de streaming
 */
export interface StreamingCallbacks {
  onStart?: (data: SSEStartData) => void;
  onToolStarted?: (data: SSEToolStartedData) => void;
  onToolCompleted?: (data: SSEToolCompletedData) => void;
  onToolFailed?: (data: SSEToolFailedData) => void;
  onAnswerDelta?: (data: SSEAnswerDeltaData) => void;
  onDone?: (data: SSEDoneData) => void;
  onError?: (data: SSEErrorData) => void;
  onConnectionError?: (error: Error) => void;
}

/**
 * Estado de conexión del chat
 */
export type ChatConnectionStatus = 'idle' | 'connecting' | 'streaming' | 'error';

/**
 * Clase de error para el servicio de chat
 */
export class ChatServiceError extends Error {
  constructor(
    message: string,
    public code?: string,
    public retryable?: boolean,
    public traceId?: string
  ) {
    super(message);
    this.name = 'ChatServiceError';
  }
}

/**
 * Servicio de chat para comunicarse con el API de aiChatBot
 *
 * Proporciona métodos para:
 * - Enviar mensajes regulares (POST /api/chat/message)
 * - Streaming de respuestas con SSE (POST /api/chat/message/stream)
 * - Limpiar conversación (DELETE /api/chat/conversation/:sessionId)
 *
 * Autenticación:
 * - Usa el token de acceso de Supabase (JWT)
 * - Se incluye en el header 'Authorization: Bearer <token>'
 */
export class ChatService {
  private static apiUrl: string = CHATBOT_API_URL;
  /**
   * Implementación de fetch inyectable. Web usa el default (global fetch);
   * React Native inyecta el de `expo/fetch` vía configure(), porque el fetch
   * global de RN no soporta streaming del body (response.body.getReader),
   * que streamTurn necesita para el SSE.
   */
  private static fetchImpl?: typeof fetch;

  /** fetch efectivo: el inyectado o el global (lazy — algunos entornos de
   *  test definen fetch después de la eval del módulo). */
  private static httpFetch(url: string, init?: RequestInit): Promise<Response> {
    const impl = this.fetchImpl ?? (globalThis as unknown as { fetch: typeof fetch }).fetch;
    return impl(url, init);
  }

  /** Plataforma del cliente (mobile la setea al configurar; ausente = web). */
  private static platform: 'web' | 'mobile' | undefined;

  /**
   * Configuración inyectable (para mobile/React Native):
   *  - apiUrl: URL del backend aiChatBot (en RN `process.env.REACT_APP_*` no
   *    existe; mobile pasa EXPO_PUBLIC_CHATBOT_URL).
   *  - fetchImpl: fetch con soporte de streaming (expo/fetch).
   *  - platform: 'mobile' hace que el bot no mande botones ver→modal de
   *    operación (en mobile solo viaja el confirmar). Web NO lo llama.
   */
  static configure(config: { apiUrl?: string; fetchImpl?: typeof fetch; platform?: 'web' | 'mobile' }): void {
    if (config.apiUrl) this.apiUrl = config.apiUrl;
    if (config.fetchImpl) this.fetchImpl = config.fetchImpl;
    if (config.platform) this.platform = config.platform;
  }

  /**
   * Resuelve una selección del chatbot (caravanas) por su selection_id.
   * Lo usa el botón "Revisar" para mostrar los animales en la pantalla destino.
   */
  static async getSelection(selectionId: string): Promise<{ selection_id: string; total: number; caravanas: string[] }> {
    const res = await this.authFetch(`${this.apiUrl}/api/chat/selection/${encodeURIComponent(selectionId)}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ChatServiceError(body?.error || 'Selección no disponible.', 'SELECTION_ERROR', res.status !== 410);
    }
    return res.json();
  }

  /**
   * Confirma (aplica) una operación pendiente del chatbot por su pending_op_id.
   * Drift-free: aplica el mismo set que se previsualizó en el dry_run.
   *
   * Con sessionId, el backend notifica al agente de ESA conversación que la op
   * se ejecutó por botón (incidente 2026-10-31): sin esto el bot confabulaba
   * el estado ("no se creó nada" sobre una carga ya hecha). Opcional: las UIs
   * viejas que no lo mandan siguen funcionando igual.
   */
  /**
   * Avisa al chatbot que el usuario IMPORTÓ una planilla con el importador de
   * Mi Ganado (flujo chat → Excel). El próximo turno del agente de ESA sesión
   * recibe un recordatorio con lo cargado — sin esto, el bot no sabe que esos
   * animales existen (la importación no pasa por el loop del LLM).
   */
  static async notifyImportedExcel(payload: {
    sessionId: string;
    createdCount: number;
    fileName?: string;
    /** Ubicaciones que mencionaba la planilla (columnas ubicación/potrero sin
     *  mapear): el agente ofrece crearlas y mover los animales ahí. */
    ubicacionesPlanilla?: string[];
  }): Promise<void> {
    const res = await this.authFetch(
      `${this.apiUrl}/api/chat/import/notify`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { 'Content-Type': 'application/json' }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ChatServiceError(body?.error || 'No se pudo notificar la importación.', 'IMPORT_NOTIFY_ERROR', res.status >= 500);
    }
  }

  static async confirmOperation(pendingOpId: string, sessionId?: string): Promise<{ success: boolean; simulated?: boolean; result: unknown }> {
    const res = await this.authFetch(
      `${this.apiUrl}/api/chat/operation/${encodeURIComponent(pendingOpId)}/confirm`,
      {
        method: 'POST',
        body: JSON.stringify(sessionId ? { sessionId } : {}),
      },
      { 'Content-Type': 'application/json' }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ChatServiceError(body?.error || 'No se pudo confirmar la operación.', 'CONFIRM_ERROR', res.status !== 410);
    }
    return res.json();
  }

  /**
   * Obtiene el token de acceso de la sesión actual de Supabase
   */
  private static async getAccessToken(): Promise<string> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        throw new ChatServiceError(
          `Error al obtener la sesión: ${error.message}`,
          'AUTH_ERROR',
          false
        );
      }

      if (!session) {
        throw new ChatServiceError(
          'No hay sesión activa. Iniciá sesión de nuevo.',
          'AUTH_REQUIRED',
          false
        );
      }

      return session.access_token;
    } catch (error) {
      if (error instanceof ChatServiceError) {
        throw error;
      }
      throw new ChatServiceError(
        error instanceof Error ? error.message : 'Error desconocido al obtener el token',
        'AUTH_ERROR',
        false
      );
    }
  }

  /**
   * Headers HTTP comunes para toda request al chatbot.
   *
   * `ngrok-skip-browser-warning` es obligatorio cuando el backend se sirve a
   * través de un túnel ngrok-free: sin ese header, ngrok devuelve su página de
   * aviso (HTML sin headers CORS) y el navegador bloquea el fetch cross-origin
   * (el error "No 'Access-Control-Allow-Origin' header is present"). Es
   * inofensivo para otros backends (lo ignoran), por eso se envía siempre.
   */
  private static buildHeaders(token?: string | null, extra: Record<string, string> = {}): Record<string, string> {
    return {
      'ngrok-skip-browser-warning': 'true',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra,
    };
  }

  /**
   * Fuerza la renovación de la sesión de Supabase (2026-09-12).
   *
   * El chatbot rechaza JWTs expirados con 401 TOKEN_EXPIRED antes de procesar;
   * como el refresh token vive en este cliente (sesión de supabase-js), la
   * renovación sólo puede ocurrir acá. Devuelve el access token nuevo, o null
   * si no se pudo renovar (sesión caída → el flujo de error normal sigue).
   */
  private static async refreshAccessToken(): Promise<string | null> {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session?.access_token) return null;
      return data.session.access_token;
    } catch {
      return null;
    }
  }

  /**
   * fetch autenticado con auto-renovación: ante un 401 del chatbot (token
   * expirado), refresca la sesión y reintenta UNA sola vez con el token
   * nuevo. Si el refresh falla, se devuelve la respuesta original para que el
   * manejo de errores de cada caller siga su curso. Sólo reintenta en 401:
   * otros estados (429 budget, 5xx) no tienen relación con la sesión.
   */
  private static async authFetch(
    url: string,
    init: RequestInit = {},
    extraHeaders: Record<string, string> = {},
    opts: { visitor?: boolean } = {}
  ): Promise<Response> {
    // Guest (sin usuario logueado): sin Authorization → el server rutea a
    // visitorAgent (asistente restringido sin tools de datos). No tocamos la
    // sesión: un guest no tiene nada que refrescar.
    if (opts.visitor) {
      return this.httpFetch(url, { ...init, headers: this.buildHeaders(null, extraHeaders) });
    }
    const token = await this.getAccessToken();
    const doFetch = (t: string | null) =>
      this.httpFetch(url, { ...init, headers: this.buildHeaders(t, extraHeaders) });

    let response = await doFetch(token);
    if (response.status === 401) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) response = await doFetch(refreshed);
    }
    return response;
  }

  /**
   * Envío streaming unificado del chat IA (KAN-218): el único camino de envío
   * para mensaje normal y drain de la cola offline. Usa authFetch (retry-401)
   * y parsea el formato SSE real del server (líneas `event:` y `data:`
   * separadas) — portado del parser inline que vivía en ChatContext.sendMessage.
   *
   * Resuelve al terminar el stream. Lanza ChatServiceError con code
   * 'BUDGET_LIMIT_EXCEEDED' (budget), 'HTTP_XXX' (error HTTP) o el code del
   * evento SSE `error`.
   */
  static async streamTurn(
    params: StreamTurnParams,
    callbacks: StreamTurnCallbacks,
    signal: AbortSignal,
    opts: { isVisitor?: boolean } = {}
  ): Promise<void> {
    const response = await this.authFetch(`${this.apiUrl}/api/chat/message/stream`, {
      method: 'POST',
      body: JSON.stringify({
        message: params.message,
        sessionId: params.sessionId,
        ...(params.batchCount && params.batchCount > 1 ? { batchCount: params.batchCount } : {}),
        ...(params.offlineQueued ? { offlineQueued: true } : {}),
        ...(params.voiceNoteCount ? { voiceNoteCount: params.voiceNoteCount } : {}),
        ...(params.transcriptionTraceIds?.length ? { transcriptionTraceIds: params.transcriptionTraceIds } : {}),
        ...(this.platform ? { platform: this.platform } : {}),
        ...(params.uiContext && Object.keys(params.uiContext).length ? { uiContext: params.uiContext } : {}),
      }),
      signal,
    }, { 'Content-Type': 'application/json' }, { visitor: !!opts.isVisitor });

    if (!response.ok) {
      let errorData: { error?: { code?: string; message?: string } } | null = null;
      try {
        errorData = JSON.parse(await response.text());
      } catch { /* error genérico */ }
      throw new ChatServiceError(
        errorData?.error?.message || `HTTP error! status: ${response.status}`,
        errorData?.error?.code || `HTTP_${response.status}`,
        true
      );
    }

    const reader = response.body?.getReader();
    if (!reader) throw new ChatServiceError('No response body reader available', 'STREAM_ERROR', false);

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventType = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('event: ')) {
          currentEventType = trimmedLine.slice(7).trim();
        } else if (trimmedLine.startsWith('data: ')) {
          try {
            const data = JSON.parse(trimmedLine.slice(6).trim());

            if (currentEventType === 'answer_delta' && data.text) {
              callbacks.onDelta(data.text);
            } else if (currentEventType === 'handoff' && data?.supportId) {
              callbacks.onHandoff(data);
            } else if (currentEventType === 'done') {
              const traceId: string | undefined = data?.trace_id || data?.metadata?.trace_id;
              const actions = data && Array.isArray(data.actions) && data.actions.length ? data.actions : undefined;
              callbacks.onDone({ ...(actions ? { actions } : {}), ...(traceId ? { traceId } : {}) });
            } else if (currentEventType === 'error') {
              if (data.code === 'BUDGET_LIMIT_EXCEEDED') {
                throw new ChatServiceError(
                  data.message || 'Has alcanzado tu límite de uso gratuito.',
                  'BUDGET_LIMIT_EXCEEDED',
                  false
                );
              }
              throw new ChatServiceError(data.message || 'Error del servidor', data.code || 'SERVER_ERROR', true);
            } else if (currentEventType === 'budget_warning') {
              callbacks.onBudgetWarning(data);
            } else if (currentEventType === 'ar_guide' && data) {
              callbacks.onARGuide(data);
            }
          } catch (e) {
            if (e instanceof ChatServiceError) throw e;
            if (!trimmedLine.includes('[DONE]')) {
              console.warn('[ChatService] Error parsing SSE line:', trimmedLine);
            }
          }
        }
      }
    }
  }

  /**
   * Transcribe una nota de voz grabada sin conexión (KAN-225). La llama el
   * drain de la cola al reconectar, antes de armar los lotes. Sube el audio
   * crudo; el server responde { text }.
   *
   * Lanza ChatServiceError con code 'CONNECTION_ERROR' ante red caída o
   * timeout (la nota queda pendiente para la próxima reconexión),
   * 'EMPTY_TRANSCRIPT' si no hubo texto, o el code del envelope / 'HTTP_XXX'.
   */
  static async transcribeVoiceNote(
    audio: Blob | ArrayBuffer,
    mimeType: string,
    durationMs: number,
    timeoutMs: number = 60000
  ): Promise<{ text: string; traceId?: string }> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      response = await this.authFetch(
        `${this.apiUrl}/api/chat/transcribe?durationMs=${Math.round(durationMs)}`,
        { method: 'POST', body: audio as BodyInit, signal: controller.signal },
        { 'Content-Type': baseMimeType(mimeType) }
      );
    } catch (error) {
      if (error instanceof ChatServiceError) throw error; // sesión / auth
      throw new ChatServiceError('Sin conexión para transcribir la nota de voz', 'CONNECTION_ERROR', true);
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      let body: { error?: { code?: string; message?: string } } | null = null;
      try {
        body = await response.json();
      } catch { /* sin envelope */ }
      throw new ChatServiceError(
        body?.error?.message || `HTTP error! status: ${response.status}`,
        body?.error?.code || `HTTP_${response.status}`,
        response.status >= 500
      );
    }

    const data: { text?: string; traceId?: string } = await response.json();
    const text = (data?.text || '').trim();
    if (!text) {
      throw new ChatServiceError('No se pudo entender la nota de voz.', 'EMPTY_TRANSCRIPT', false);
    }
    return { text, ...(data?.traceId ? { traceId: data.traceId } : {}) };
  }

  /**
   * Envía un mensaje al chat y recibe la respuesta completa
   *
   * @param messageData - Datos del mensaje a enviar
   * @returns Respuesta completa del chat
   */
  static async sendMessage(messageData: ChatMessage): Promise<ChatResponse> {
    try {
      const response = await this.authFetch(`${this.apiUrl}/api/chat/message`, {
        method: 'POST',
        body: JSON.stringify({
          message: messageData.message.trim(),
          sessionId: messageData.sessionId,
          provider: messageData.provider || 'mimo',
          model: messageData.model,
          temperature: messageData.temperature,
        }),
      }, { 'Content-Type': 'application/json' });

      const data: ChatResponse = await response.json();

      if (!response.ok) {
        const errorResponse = data as ChatErrorResponse;
        throw new ChatServiceError(
          errorResponse.error.message || 'Error en la respuesta del servidor',
          errorResponse.error.code,
          errorResponse.error.retryable,
          errorResponse.metadata.trace_id
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ChatServiceError) {
        throw error;
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ChatServiceError(
          'No se pudo conectar con el chat. Revisá tu conexión.',
          'CONNECTION_ERROR',
          true
        );
      }

      throw new ChatServiceError(
        error instanceof Error ? error.message : 'Error desconocido',
        'INTERNAL',
        true
      );
    }
  }

  /**
   * Envía un mensaje y recibe la respuesta en streaming usando SSE
   *
   * @param messageData - Datos del mensaje a enviar
   * @param callbacks - Funciones callback para manejar los eventos SSE
   * @returns AbortController para cancelar el stream
   */
  static async sendMessageStream(
    messageData: ChatMessage,
    callbacks: StreamingCallbacks
  ): Promise<AbortController> {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const response = await this.authFetch(`${this.apiUrl}/api/chat/message/stream`, {
        method: 'POST',
        body: JSON.stringify({
          message: messageData.message.trim(),
          sessionId: messageData.sessionId,
          provider: messageData.provider || 'mimo',
          model: messageData.model,
          temperature: messageData.temperature,
        }),
        signal,
      }, { 'Content-Type': 'application/json' });

      if (!response.ok) {
        let errorMessage = 'Error en el streaming';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error?.message || errorMessage;
        } catch {}
        throw new ChatServiceError(errorMessage, 'STREAM_ERROR', !response.headers.get('content-type')?.includes('text/event-stream'));
      }

      if (!response.body) {
        throw new ChatServiceError('No se puede leer el cuerpo de la respuesta', 'STREAM_ERROR', false);
      }

      // Procesar el stream SSE
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.trim()) continue;

              const eventMatch = line.match(/^event:\s*(.+)$/);
              const dataMatch = line.match(/^data:\s*(.+)$/);

              if (eventMatch) {
                // Próxima línea será data
                continue;
              }

              if (dataMatch) {
                try {
                  const eventData = JSON.parse(dataMatch[1]);
                  this.handleSSEEvent(eventData, callbacks, messageData);
                } catch (parseError) {
                  console.warn('[ChatService] Error al parsear evento SSE:', parseError);
                }
              }
            }
          }
        } catch (error) {
          if (!signal.aborted && callbacks.onConnectionError) {
            callbacks.onConnectionError(
              error instanceof Error ? error : new Error('Error en el stream')
            );
          }
        }
      };

      processStream().catch((error) => {
        if (!signal.aborted && callbacks.onConnectionError) {
          callbacks.onConnectionError(error);
        }
      });

      return controller;
    } catch (error) {
      if (error instanceof ChatServiceError && !signal.aborted) {
        if (callbacks.onError) {
          callbacks.onError({
            code: error.code || 'INTERNAL',
            message: error.message,
          });
        }
        throw error;
      }

      if (!signal.aborted) {
        const connectionError = new ChatServiceError(
          error instanceof Error ? error.message : 'Error de conexión',
          'CONNECTION_ERROR',
          true
        );
        if (callbacks.onConnectionError) {
          callbacks.onConnectionError(connectionError);
        }
        throw connectionError;
      }

      return controller;
    }
  }

  /**
   * Maneja un evento SSE y llama al callback correspondiente
   */
  private static handleSSEEvent(
    eventData: unknown,
    callbacks: StreamingCallbacks,
    originalMessage: ChatMessage
  ): void {
    // Los eventos SSE pueden venir con diferentes estructuras
    // El formato esperado es { event: 'tipo', data: {...} }
    // pero también puede venir data directo con el tipo implícito

    if (typeof eventData !== 'object' || eventData === null) {
      return;
    }

    const data = eventData as Record<string, unknown>;

    if (data.event === 'start' && callbacks.onStart) {
      callbacks.onStart(data.data as SSEStartData);
    } else if (data.event === 'tool_started' && callbacks.onToolStarted) {
      callbacks.onToolStarted(data.data as SSEToolStartedData);
    } else if (data.event === 'tool_completed' && callbacks.onToolCompleted) {
      callbacks.onToolCompleted(data.data as SSEToolCompletedData);
    } else if (data.event === 'tool_failed' && callbacks.onToolFailed) {
      callbacks.onToolFailed(data.data as SSEToolFailedData);
    } else if (data.event === 'answer_delta' && callbacks.onAnswerDelta) {
      callbacks.onAnswerDelta(data.data as SSEAnswerDeltaData);
    } else if (data.event === 'done' && callbacks.onDone) {
      callbacks.onDone(data.data as SSEDoneData);
    } else if (data.event === 'error' && callbacks.onError) {
      callbacks.onError(data.data as SSEErrorData);
    }
  }

  /**
   * Envia feedback (thumbs up/down + comentario) para un mensaje del asistente
   *
   * @param traceId - ID de traza del mensaje devuelto por el backend en el evento `done`
   * @param feedbackThumb - true = thumbs up, false = thumbs down
   * @param feedbackText - Comentario opcional (suele acompañar al thumbs down)
   */
  static async submitFeedback(
    traceId: string,
    feedbackThumb: boolean,
    feedbackText: string | null
  ): Promise<void> {
    try {
      const token = await this.getAccessToken();

      const response = await this.httpFetch(`${this.apiUrl}/api/chat/feedback`, {
        method: 'POST',
        headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ traceId, feedbackThumb, feedbackText }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new ChatServiceError(
          result?.error?.message || 'Error al enviar feedback',
          'FEEDBACK_ERROR',
          true,
          traceId
        );
      }
    } catch (error) {
      if (error instanceof ChatServiceError) {
        throw error;
      }
      throw new ChatServiceError(
        error instanceof Error ? error.message : 'Error desconocido',
        'INTERNAL',
        true
      );
    }
  }

  /**
   * Solicita handoff a humano (live chat con soporte).
   *
   * Productor logueado: viaja el JWT en Authorization (vía getAccessToken) y el
   * server resuelve identidad desde el token.
   * Visitor (sin sesión): getAccessToken lanza → cae al `.catch(() => null)` →
   * `buildHeaders(null)` omite Authorization → el server exige visitorName /
   * visitorEmail en el body (flujo visitor).
   */
  static async requestHandoff(payload: {
    initialMessage: string;
    contextSummary?: string;
    originSessionId?: string;
    visitorName?: string;
    visitorEmail?: string;
  }): Promise<{ supportId: string; status: string }> {
    const token = await this.getAccessToken().catch(() => null);
    const res = await this.httpFetch(`${this.apiUrl}/api/chat/handoff/request`, {
      method: 'POST',
      headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    const body = await res.json();
    if (!res.ok || !body.success) {
      throw new ChatServiceError(
        body?.error?.message || 'handoff request failed',
        body?.error?.code || 'HANDOFF_ERROR'
      );
    }
    return body.data;
  }

  /**
   * Devuelve el handoff activo para la identidad actual (si existe).
   * Visitors no usan este endpoint (server devuelve null), pero se mantiene
   * seguro con `.catch(() => null)`.
   */
  static async getActiveHandoff(): Promise<{
    supportId: string;
    status: string;
    messages: unknown[];
    meta: unknown;
  } | null> {
    const token = await this.getAccessToken().catch(() => null);
    const res = await this.httpFetch(`${this.apiUrl}/api/chat/handoff/active`, {
      headers: this.buildHeaders(token),
    });
    const body = await res.json();
    if (!res.ok || !body.success) {
      throw new ChatServiceError('handoff active failed', 'HANDOFF_ERROR');
    }
    return body.data || null;
  }

  /**
   * Historial de un hilo de soporte (meta + mensajes). Se usa al recibir un
   * outreach proactivo del equipo para mostrar el mensaje que lo originó.
   */
  static async getHandoffHistory(supportId: string): Promise<{
    meta: unknown;
    messages: Array<{ ts?: string; from?: string; text?: string }>;
  } | null> {
    const token = await this.getAccessToken().catch(() => null);
    const res = await this.httpFetch(`${this.apiUrl}/api/chat/handoff/${encodeURIComponent(supportId)}/history`, {
      headers: this.buildHeaders(token),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body || !body.success) return null;
    return body.data || null;
  }

  /**
   * Envía un mensaje al hilo de soporte (fallback REST del evento `message` del
   * socket). Se usa cuando el socket no está conectado.
   */
  static async sendHandoffMessage(supportId: string, text: string): Promise<void> {
    const token = await this.getAccessToken().catch(() => null);
    await this.httpFetch(`${this.apiUrl}/api/chat/handoff/${encodeURIComponent(supportId)}/message`, {
      method: 'POST',
      headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({ supportId, text }),
    });
  }

  /**
   * Cierra el handoff desde el lado del productor (fallback REST del evento
   * `close` del socket).
   */
  static async closeHandoff(supportId: string): Promise<void> {
    const token = await this.getAccessToken().catch(() => null);
    await this.httpFetch(`${this.apiUrl}/api/chat/handoff/${encodeURIComponent(supportId)}/close`, {
      method: 'POST',
      headers: this.buildHeaders(token),
    });
  }

  /**
   * Limpia el historial de conversación para una sesión
   *
   * @param sessionId - ID de la sesión a limpiar
   */
  static async clearConversation(sessionId: string): Promise<{ success: boolean; sessionId: string }> {
    try {
      const token = await this.getAccessToken();

      const response = await this.httpFetch(`${this.apiUrl}/api/chat/conversation/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        headers: this.buildHeaders(token, { 'Content-Type': 'application/json' }),
      });

      if (!response.ok) {
        throw new ChatServiceError(
          `Error al limpiar la conversación: ${response.statusText}`,
          'DELETE_ERROR',
          true
        );
      }

      const result = await response.json();
      return {
        success: result.success,
        sessionId,
      };
    } catch (error) {
      if (error instanceof ChatServiceError) {
        throw error;
      }
      throw new ChatServiceError(
        error instanceof Error ? error.message : 'Error desconocido',
        'INTERNAL',
        true
      );
    }
  }

  /**
   * Verifica la salud del servidor de chat
   *
   * @returns Estado del servidor
   */
  static async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    llm_providers?: unknown[];
    mcp_tools?: number;
    redis?: string;
  } | null> {
    try {
      const response = await this.httpFetch(`${this.apiUrl}/api/chat/health`, {
        method: 'GET',
        headers: this.buildHeaders(),
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.warn('[ChatService] Health check failed:', error);
      return null;
    }
  }

  /**
   * Genera un nuevo ID de sesión
   */
  static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Actualiza la URL base del API (útil para testing)
   */
  static setApiUrl(url: string): void {
    this.apiUrl = url;
  }

  /**
   * Obtiene la URL base del API actual
   */
  static getApiUrl(): string {
    return this.apiUrl;
  }
}

export default ChatService;
